import { Sparkles, Coins, Layers, Droplet, Gem, Copy, Upload, TrendingUp } from 'lucide-react';
import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useCollection } from '@/hooks/useCollection';
import { formatPrice } from '@/lib/card-prices';
import { collectionLineValue, collectionUnitPrice } from '@/lib/collectionValue';
import { PriceMoversCard } from '@/components/PriceMoversCard';
import { ExportCollectionButton } from '@/components/ExportCollectionButton';

export function CollectionInsights() {
  const { data: collection } = useCollection();

  const insights = useMemo(() => {
    const items = collection || [];
    let totalValue = 0;
    let totalCards = 0;
    let inkable = 0;
    let nonInkable = 0;
    let enchantedValue = 0;
    const valuedItems: { name: string; value: number; ink: string }[] = [];
    const rarityCounts: Record<string, number> = {};
    const duplicates: { name: string; extra: number; value: number }[] = [];

    for (const item of items) {
      const c = item.card;
      if (!c) continue;
      const qty = item.quantity || 1;
      const lineValue = collectionLineValue(item);
      totalValue += lineValue;
      totalCards += qty;
      if (c.inkable) inkable += qty; else nonInkable += qty;
      if (c.enchanted || item.print?.treatment === 'enchanted') enchantedValue += lineValue;
      if (collectionUnitPrice(item) > 0) valuedItems.push({ name: c.name, value: lineValue, ink: c.ink_color });
      const r = (c.rarity || 'unknown').toLowerCase();
      rarityCounts[r] = (rarityCounts[r] || 0) + qty;
      if (qty > 4) {
        const extra = qty - 4;
        duplicates.push({ name: c.name, extra, value: collectionUnitPrice(item) * extra });
      }
    }

    valuedItems.sort((a, b) => b.value - a.value);
    duplicates.sort((a, b) => b.extra - a.extra || b.value - a.value);

    return {
      totalValue,
      totalCards,
      uniqueCards: items.length,
      inkable,
      nonInkable,
      enchantedValue,
      topValued: valuedItems.slice(0, 3),
      rarityCounts,
      duplicates: duplicates.slice(0, 5),
      duplicateTotal: duplicates.reduce((s, d) => s + d.extra, 0),
      duplicateValue: duplicates.reduce((s, d) => s + d.value, 0),
    };
  }, [collection]);

  if (!collection?.length) return null;

  const inkablePct = insights.totalCards > 0 ? Math.round((insights.inkable / insights.totalCards) * 100) : 0;

  return (
    <div className="mt-4 rounded-2xl border border-border bg-gradient-to-br from-card via-card to-primary/5 p-4 animate-fade-in">
      <div className="flex items-center gap-1.5 mb-3">
        <Sparkles className="h-4 w-4 text-primary" />
        <h3 className="font-display text-sm font-semibold text-foreground">Collection Insights</h3>
      </div>

      {/* Import / Export / Value chart */}
      <div className="mb-3 flex flex-wrap gap-2">
        <Link
          to="/collection/import"
          className="flex items-center gap-1.5 rounded-lg bg-primary/10 px-2.5 py-1.5 text-[11px] font-medium text-primary font-body hover:bg-primary/20 transition-colors"
        >
          <Upload className="h-3 w-3" /> Import CSV
        </Link>
        <ExportCollectionButton />
        <Link
          to="/collection/value"
          className="flex items-center gap-1.5 rounded-lg bg-secondary px-2.5 py-1.5 text-[11px] font-medium text-foreground font-body hover:bg-accent transition-colors"
        >
          <TrendingUp className="h-3 w-3" /> Value chart
        </Link>
      </div>

      {/* Headline value */}
      <div className="rounded-xl bg-background/60 border border-border p-3 mb-3">
        <div className="flex items-baseline gap-2">
          <Coins className="h-4 w-4 text-primary" />
          <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-body">Estimated value</p>
        </div>
        <p className="font-display text-2xl font-bold text-primary mt-1">{formatPrice(insights.totalValue)}</p>
        <p className="text-[10px] text-muted-foreground font-body mt-0.5">
          {insights.totalCards} cards · {insights.uniqueCards} unique
          {insights.enchantedValue > 0 && ` · ${formatPrice(insights.enchantedValue)} in enchanted`}
        </p>
      </div>

      {/* Inkable split */}
      <div className="rounded-xl bg-background/60 border border-border p-3 mb-3">
        <div className="flex items-center justify-between mb-1.5">
          <div className="flex items-center gap-1.5">
            <Droplet className="h-3.5 w-3.5 text-primary" />
            <span className="text-[11px] font-medium text-foreground font-body">Inkable / Non-inkable</span>
          </div>
          <span className="text-[10px] text-muted-foreground font-body">{insights.inkable} / {insights.nonInkable}</span>
        </div>
        <div className="h-2 rounded-full bg-secondary overflow-hidden flex">
          <div className="h-full bg-primary transition-all" style={{ width: `${inkablePct}%` }} />
          <div className="h-full bg-muted-foreground/40 transition-all" style={{ width: `${100 - inkablePct}%` }} />
        </div>
      </div>

      {/* Top valued cards */}
      {insights.topValued.length > 0 && (
        <div className="rounded-xl bg-background/60 border border-border p-3 mb-3">
          <div className="flex items-center gap-1.5 mb-2">
            <Layers className="h-3.5 w-3.5 text-primary" />
            <span className="text-[11px] font-medium text-foreground font-body">Top-valued cards</span>
          </div>
          <ul className="space-y-1.5">
            {insights.topValued.map((c, i) => (
              <li key={i} className="flex items-center justify-between text-[11px] font-body">
                <span className="text-foreground truncate flex-1">{c.name}</span>
                <span className="text-primary font-medium ml-2">{formatPrice(c.value)}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Rarity breakdown */}
      {Object.keys(insights.rarityCounts).length > 0 && (
        <div className="rounded-xl bg-background/60 border border-border p-3 mb-3">
          <div className="flex items-center gap-1.5 mb-2">
            <Gem className="h-3.5 w-3.5 text-primary" />
            <span className="text-[11px] font-medium text-foreground font-body">Rarity breakdown</span>
          </div>
          <div className="flex h-2 w-full overflow-hidden rounded-full bg-secondary">
            {(['common', 'uncommon', 'rare', 'super_rare', 'legendary', 'enchanted'] as const).map(r => {
              const n = insights.rarityCounts[r] || 0;
              if (!n) return null;
              const pct = (n / insights.totalCards) * 100;
              const colorMap: Record<string, string> = {
                common: 'bg-muted-foreground/40',
                uncommon: 'bg-ink-emerald',
                rare: 'bg-ink-sapphire',
                super_rare: 'bg-ink-amethyst',
                legendary: 'bg-ink-amber',
                enchanted: 'bg-ink-ruby',
              };
              return <div key={r} className={`h-full ${colorMap[r]}`} style={{ width: `${pct}%` }} title={`${r}: ${n}`} />;
            })}
          </div>
          <div className="mt-2 grid grid-cols-3 gap-x-2 gap-y-1 text-[10px] font-body">
            {Object.entries(insights.rarityCounts)
              .sort((a, b) => b[1] - a[1])
              .map(([r, n]) => (
                <div key={r} className="flex justify-between">
                  <span className="text-muted-foreground capitalize truncate">{r.replace('_', ' ')}</span>
                  <span className="text-foreground font-medium">{n}</span>
                </div>
              ))}
          </div>
        </div>
      )}

      {/* Duplicates report */}
      {insights.duplicates.length > 0 && (
        <div className="rounded-xl bg-background/60 border border-border p-3 mb-3">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-1.5">
              <Copy className="h-3.5 w-3.5 text-primary" />
              <span className="text-[11px] font-medium text-foreground font-body">Duplicates (beyond playset of 4)</span>
            </div>
            <span className="text-[10px] text-muted-foreground font-body">
              {insights.duplicateTotal} extra{insights.duplicateValue > 0 && ` · ${formatPrice(insights.duplicateValue)}`}
            </span>
          </div>
          <ul className="space-y-1">
            {insights.duplicates.map((d, i) => (
              <li key={i} className="flex items-center justify-between text-[11px] font-body">
                <span className="text-foreground truncate flex-1">{d.name}</span>
                <span className="text-muted-foreground ml-2">+{d.extra}{d.value > 0 && ` · ${formatPrice(d.value)}`}</span>
              </li>
            ))}
          </ul>
          <p className="mt-2 text-[10px] text-muted-foreground font-body">
            Tip: list extras on the Trades tab to swap them for cards you need.
          </p>
        </div>
      )}

      {/* Weekly gainers/losers */}
      <PriceMoversCard />
    </div>
  );
}
